import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { getUserAiConfig } from '../endpoints/user/ai-config_GET.schema';
import {
  postUserAiConfig,
  InputType as AiConfigInput,
} from '../endpoints/user/ai-config_POST.schema';
import { getIntegrationsList } from '../endpoints/integrations/list_GET.schema';
import { postDeleteIntegration } from '../endpoints/integrations/delete_POST.schema';
import { getGitHubAuthorizeUrl } from '../endpoints/integrations/github_authorize_GET.schema';
import { toast } from 'sonner';
import { useEffect } from 'react';

export const USER_AI_CONFIG_QUERY_KEY = ['user', 'ai-config'] as const;
export const USER_INTEGRATIONS_QUERY_KEY = ['user', 'integrations'] as const;

export function useUserAiConfig() {
  return useQuery({
    queryKey: USER_AI_CONFIG_QUERY_KEY,
    queryFn: () => getUserAiConfig(),
    retry: 1,
  });
}

export function useUpdateUserAiConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AiConfigInput) => postUserAiConfig(data),
    onSuccess: (data) => {
      queryClient.setQueryData(USER_AI_CONFIG_QUERY_KEY, data);
      toast.success('AI configuration updated');
    },
    onError: (error) => {
      toast.error(`Failed to update AI config: ${error.message}`);
    },
  });
}

export function useUserIntegrations() {
  return useQuery({
    queryKey: USER_INTEGRATIONS_QUERY_KEY,
    queryFn: () => getIntegrationsList(),
    retry: 1,
  });
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (provider: 'github' | 'gitlab' | 'atlassian' | 'supabase') =>
      postDeleteIntegration({ provider }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_QUERY_KEY });
      toast.success('Integration removed');
    },
    onError: (error) => {
      toast.error(`Failed to remove integration: ${error.message}`);
    },
  });
}

export function useConnectGitHub() {
  const queryClient = useQueryClient();

  const connect = () => {
    const url = getGitHubAuthorizeUrl();
    // Larger popup for GitHub's UI
    const width = 800;
    const height = 900;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // Open in a popup window
    // For webviews, this may open in the system browser which is actually preferred
    const popup = window.open(
      url,
      'GitHub Integration',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
    );

    // If popup was blocked or in a restricted environment, try direct navigation
    if (!popup || popup.closed) {
      // Store current URL to return to after auth
      sessionStorage.setItem('github_auth_return_url', window.location.href);
      // Navigate directly - user will be redirected back
      window.location.href = url;
    } else {
      // Poll to detect when popup closes
      const pollInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollInterval);
          // Refresh integrations when popup closes - user may have completed installation
          queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_QUERY_KEY });
        }
      }, 500);

      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    // Check for github_connected query param (when redirected back from callback)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('github_connected') === '1') {
      toast.success('GitHub connected successfully');
      queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_QUERY_KEY });
      // Clean up URL
      urlParams.delete('github_connected');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    const handleMessage = (event: MessageEvent) => {
      // Accept messages from same origin or cross-origin popup callbacks
      if (event.origin !== window.location.origin) {
        // Still allow messages that look like valid integration responses
        if (!event.data?.type?.startsWith('INTEGRATION_')) return;
      }

      const data = event.data;
      if (data?.type === 'INTEGRATION_SUCCESS' && data.provider === 'github') {
        toast.success('GitHub connected successfully');
        queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_QUERY_KEY });
      } else if (data?.type === 'INTEGRATION_ERROR') {
        toast.error(`Integration failed: ${data.error}`);
      } else if (data?.type === 'INTEGRATION_LOGIN_REQUIRED') {
        toast.info('Please log in to complete GitHub integration');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient]);

  return { connect };
}
